import { CdkTableModule } from '@angular/cdk/table';
import { CommonModule } from '@angular/common';
import { ModuleWithProviders, NgModule } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { NgChartsModule } from 'ng2-charts';
import { MarkdownModule } from 'ngx-markdown';
import {
  DateFnsAdapter,
  MAT_DATE_FNS_FORMATS,
  MatDateFnsModule,
} from '@angular/material-date-fns-adapter';
import { AutocompleteSearchComponent } from './autocomplete-search/autocomplete-search.component';
import { AvatarStackComponent } from './avatar-stack/avatar-stack.component';
import { AvatarComponent } from './avatar/avatar.component';
import { BadgeComponent } from './badge/badge.component';
import { BarLoaderComponent } from './bar-loader/bar-loader.component';
import { CircularLoaderComponent } from './circular-loader/circular-loader.component';
import { ConfirmationDialogComponent } from './confirmation-dialog/confirmation-dialog.component';
import { CustomerSelectionViewComponent } from './customer-selection-view/customer-selection-view.component';
import { ElementLoaderComponent } from './element-loader/element-loader.component';
import { FileCardComponent } from './file-card/file-card.component';
import { FileIconComponent } from './file-icon/file-icon.component';
import { FilePickerComponent } from './file-picker/file-picker.component';
import {
  OrganisationFormComponent,
  OrganisationPreferencesFormComponent,
  TenantFormComponent,
} from './forms/index';
import { StaffFormComponent } from './forms/staff/staff-form.component';
import { GUARDS } from './guards';
import { HasQualificationDirective } from './has-qualification/has-qualification.directive';
import { RoleDirective } from './has-role/has-role.directive';
import { GroupAdminDirective } from './group-admin/group-admin.directive';
import { HeartButtonComponent } from './heart-button/heart-button.component';
import { IconComponent } from './icon/icon.component';
import { ImageLoaderComponent } from './image-loader/image-loader.component';
import { InviteActionsComponent } from './invite-actions/invite-actions.component';
import { InviteComponent } from './invite/invite.component';
import { LinkCardComponent } from './link-card/link-card.component';
import { MaterialModule } from './material.module';
import { OrganisationConfigDirective } from './organisation-config/organisation-config.directive';
import { PhotoPickerItemComponent } from './photo-picker/photo-picker.component';
import { PIPES, RolePipe } from './pipes/index';
import { ProfileImageComponent } from './profile-image/profile-image.component';
import { RatingComponent } from './rating/rating.component';
import { RemoteConfigDirective } from './remote-config/remote-config.directive';
import { RowResultComponent } from './row-result/row-result.component';
import { SELECTORS } from './selectors/index';
import { SentResetComponent } from './sent-reset/sent-reset.component';
import { RestartModalComponent } from './services/app-restart/restart-modal.component';
import { AppRestartService, GoogleGeocodeService } from './services/index';
import { AppSettings } from './settings/app-settings.service';
import { CsvPreviewComponent } from './tables/csv-preview/csv-preview.component';
import {
  DataListCellPlaceholderDirective,
  DataListCellRendererDirective,
  DataListSimpleCellComponent,
  DataListTableComponent,
} from './tables/data-list-table/data-list-table.component';
import { OrganisationsTableComponent } from './tables/index';
import { MemoCategoriesTableComponent } from './tables/memo-categories/memo-categories-table.component';
import { ListTileComponent } from './list-tile/list-tile.component';
import { LottieModule } from 'ngx-lottie';
import player from 'lottie-web';
import { MemoActionIconComponent } from './memo-action-icon/memo-action-icon.component';
import { SelectRoleDialogComponent } from './select-role-dialog/select-role-dialog.component';
import { IntersectionObserverDirective } from './intersection-observer/intersection-observer.directive';
import { BrandingComponent } from '../organisation/branding/branding.component';
import { ColorPickerModule } from 'ngx-color-picker';
import { TenantsTableComponent } from './tables/tenants/tenants-table.component';
import { OrganisationListSelectDialogComponent } from './organisation-list-dialog/organisation-list-select-dialog.component';
import { OrganisationFilterPipe } from './organisation-filter-pipe/organisation-filter.pipe';
import { InvolvedInOrgsPipe } from './involved-in-orgs-pipe/involved-in-orgs.pipe';
import { OrganisationSortPipe } from './organisation-sort-pipe/organisation-sort.pipe';
import { MemberImportCsvPreviewComponent } from './tables/bulk-member-import-table/member-import-csv-preview.component';
import { MemberImportComponent } from './member-import/member-import.component';
import { EasterCategoryComponent } from './animated-category/animated-category.component';
import { CustomerFilterComponent } from './customer-filter/customer-filter.component';
import { CustomerSelectDialogComponent } from './customer-select/customer-select-dialog.component';
import { SelectTriggerComponent } from './select-trigger/select-trigger.component';
import { OrganisationPreferenceDirective } from './organisation-preference/organisation-preference.directive';
import { InputConfirmationDialogComponent } from './input-confirmation-dialog/input-confirmation-dialog.component';
import { AutoAnimateDirective } from './auto-animate/auto-animate.directive';
import { DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE } from '@angular/material/core';
import { enAU } from 'date-fns/locale';
import { DataListImageCellComponent } from './tables/data-list-table/image-cell/data-list-image-cell.component';
import { EntityHistoryDialogComponent } from './entity-history-dialog/entity-history-dialog.component';
import { EntityHistoryItemComponent } from './entity-history-item/entity-history-item.component';
import { FileHistoryDialogComponent } from './file-history-dialog/file-history-dialog.component';

// Note we need a separate function as it's required
// by the AOT compiler.
export function playerFactory() {
  return player;
}

/**
 * Do not specify providers for modules that might be imported by a lazy loaded module.
 */

const COMPONENTS = [
  AutoAnimateDirective,
  AvatarComponent,
  AvatarStackComponent,
  BadgeComponent,
  BarLoaderComponent,
  BrandingComponent,
  FileIconComponent,
  CircularLoaderComponent,
  ConfirmationDialogComponent,
  InputConfirmationDialogComponent,
  CsvPreviewComponent,
  CustomerFilterComponent,
  CustomerSelectionViewComponent,
  DataListTableComponent,
  DataListCellRendererDirective,
  DataListCellPlaceholderDirective,
  DataListImageCellComponent,
  DataListSimpleCellComponent,
  ElementLoaderComponent,
  EntityHistoryDialogComponent,
  EntityHistoryItemComponent,
  FileCardComponent,
  FileHistoryDialogComponent,
  FilePickerComponent,
  HasQualificationDirective,
  HeartButtonComponent,
  GroupAdminDirective,
  RoleDirective,
  RemoteConfigDirective,
  IconComponent,
  ImageLoaderComponent,
  InviteComponent,
  InviteActionsComponent,
  InvolvedInOrgsPipe,
  LinkCardComponent,
  MemoCategoriesTableComponent,
  MemberImportCsvPreviewComponent,
  MemberImportComponent,
  OrganisationConfigDirective,
  OrganisationFilterPipe,
  OrganisationFormComponent,
  OrganisationPreferencesFormComponent,
  OrganisationsTableComponent,
  OrganisationListSelectDialogComponent,
  OrganisationPreferenceDirective,
  OrganisationSortPipe,
  PhotoPickerItemComponent,
  ProfileImageComponent,
  RatingComponent,
  RestartModalComponent,
  RowResultComponent,
  SentResetComponent,
  StaffFormComponent,
  TenantsTableComponent,
  TenantFormComponent,
  AutocompleteSearchComponent,
  ListTileComponent,
  MemoActionIconComponent,
  SelectRoleDialogComponent,
  SelectTriggerComponent,
  IntersectionObserverDirective,
  EasterCategoryComponent,
  CustomerSelectDialogComponent,
  ...SELECTORS,
];
@NgModule({
    imports: [
        NgChartsModule,
        CommonModule,
        RouterModule,
        FormsModule,
        ColorPickerModule,
        MaterialModule,
        MarkdownModule.forChild(),
        MatDateFnsModule,
        ReactiveFormsModule,
        TranslateModule,
        CdkTableModule,
        LottieModule.forRoot({ player: playerFactory }),
    ],
    declarations: [...COMPONENTS, ...PIPES],
    exports: [
        ...COMPONENTS,
        ...PIPES,
        CommonModule,
        FormsModule,
        ReactiveFormsModule,
        MaterialModule,
    ],
    providers: [
        ...GUARDS,
        AppRestartService,
        GoogleGeocodeService,
        AppRestartService,
        RolePipe,
        { provide: MAT_DATE_LOCALE, useValue: enAU },
        { provide: DateAdapter, useClass: DateFnsAdapter, deps: [MAT_DATE_LOCALE] },
        { provide: MAT_DATE_FORMATS, useValue: MAT_DATE_FNS_FORMATS },
    ]
})
export class SharedModule {
  static forRoot(): ModuleWithProviders<SharedModule> {
    return {
      ngModule: SharedModule,
      providers: [AppSettings, { provide: 'Window', useValue: window }],
    };
  }
}
